import { useMutation } from '@apollo/client';
import React, { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Icon, IconClose } from '@flixbus/honeycomb-icons-react';
import {
	Button,
	Checkbox,
	FormRow,
	Heading,
	Input,
	Popup,
	PopupSection,
} from '@flixbus/honeycomb-react';

import LocationSearch from 'common/LocationSearch';
import {
	dateToHoneycombInput,
	getDayAfterDaysPeriod,
	getLimitDate,
	isTimestampBefore,
	resetTimestampToUtc,
} from 'utils/datetime';

import { DEFAULT_VALUES } from './const';
import CREATE_CREW_MEMBER_HOME_BASE from './createCrewMemberHomeBase';

import styles from './styles.module.css';

/**
 * PROPS (ActiveElements + page):
 * - employeeId: string (required)
 * - hasPermanentLocation?: boolean = true
 * - onActionSuccess?: () => void
 * - onError?: () => void
 * - onClose: () => void (injected by Host)
 */

const ModalSetHomeLocation = ({
	employeeId,
	onClose = () => {},
	hasPermanentLocation = true,
	onActionSuccess = () => {},
	onError = () => {},
}) => {
	const targetRef = useRef(null);
	const [isTemporaryHomebase, setIsTemporaryHomebase] = useState(false);

	const minEffectiveDate = hasPermanentLocation
		? dateToHoneycombInput(getDayAfterDaysPeriod(new Date(), 1))
		: false;

	const {
		control,
		resetField,
		reset,
		getValues,
		setValue,
		formState: { dirtyFields },
	} = useForm({
		mode: 'onChange',
		defaultValues: DEFAULT_VALUES,
	});

	const [createHomeBase, { loading }] = useMutation(CREATE_CREW_MEMBER_HOME_BASE, {
		onCompleted: () => {
			onActionSuccess();
			onClose();
			reset(DEFAULT_VALUES);
			setIsTemporaryHomebase(false);
		},
		onError: () => {
			onError();
			reset(DEFAULT_VALUES);
			setIsTemporaryHomebase(false);
		},
		refetchQueries: ['GetCrewMemberHomeBases', 'GetCrewMember'],
		fetchPolicy: 'network-only',
	});

	const handleConfirm = () => {
		const { cityId, effectiveTimestamp, expireTimestamp } = getValues();
		if (!cityId || !effectiveTimestamp) return;

		createHomeBase({
			variables: {
				input: {
					crewMemberId: employeeId, // ← from props
					cityId: cityId.key ?? cityId.value, // support both shapes
					effectiveTimestamp: resetTimestampToUtc(effectiveTimestamp),
					...(expireTimestamp && {
						expireTimestamp: resetTimestampToUtc(expireTimestamp),
					}),
				},
			},
		});
	};

	const handleResetIcon = (fieldName) => (
		<Button onClick={() => resetField(fieldName)}>
			<Icon InlineIcon={IconClose} className={styles.alignIcon} />
		</Button>
	);

	const handleTemporaryCheck = () => {
		const next = !isTemporaryHomebase;
		setIsTemporaryHomebase(next);
		if (!next) {
			resetField('expireTimestamp');
		}
	};

	const validateIsTemporary = (value) => {
		const effectiveTimestamp = getValues()['effectiveTimestamp'];
		// valid only when temporary & expire > effective
		return effectiveTimestamp
			? isTemporaryHomebase && isTimestampBefore(effectiveTimestamp, value)
			: isTemporaryHomebase;
	};

	const isReadyForSubmission = () => {
		const { effectiveTimestamp, expireTimestamp, cityId } = getValues();
		if (isTemporaryHomebase) {
			return !!(effectiveTimestamp && expireTimestamp && cityId);
		}
		return !!(effectiveTimestamp && cityId);
	};

	const handleCloseAndReset = () => {
		reset(DEFAULT_VALUES);
		setIsTemporaryHomebase(false);
		onClose();
	};

	return (
		<Popup
			id="move-to-location"
			aria-labelledby="move-to-location"
			aria-describedby="move-to-location"
			active
			onOverlayClick={handleCloseAndReset}
			targetRef={targetRef}
			extraClasses={styles.moveToLocationWrapper}
		>
			<PopupSection type="title">
				<Heading id="move-to-location-header" size={1} sectionHeader flushSpace>
					Change home domicile
				</Heading>
			</PopupSection>

			<PopupSection extraClasses={styles.antiScroll} type="content" id="move-to-form">
				<div className={styles.header}>
					Specify the new location and period for the crew member.
				</div>

				<form id="createNewLocation">
					{/* Effective date */}
					<Controller
						disabled={loading}
						rules={{ required: true }}
						name="effectiveTimestamp"
						control={control}
						render={({ field: { ref: _ref, ...field }, fieldState }) => {
							const { isDirty, invalid } = fieldState;
							const inputProps = {
								id: field.name,
								label: 'Effective date',
								placeholder: 'dd.mm.yyy',
								type: 'date',
							};
							if (minEffectiveDate) {
								inputProps.min = minEffectiveDate;
							}
							const expireTimestamp = getValues('expireTimestamp');
							if (expireTimestamp) {
								inputProps.max = expireTimestamp;
							} else {
								inputProps.max = getLimitDate();
							}
							if (isDirty) {
								inputProps.valid = !invalid;
								inputProps.iconRight = handleResetIcon(field.name);
							}

							return (
								<FormRow>
									<Input {...field} {...inputProps} />
								</FormRow>
							);
						}}
					/>

					{/* Expiration date (only when temporary) */}
					{isTemporaryHomebase && (
						<Controller
							disabled={loading}
							rules={{
								validate: (val) => validateIsTemporary(val),
							}}
							name="expireTimestamp"
							control={control}
							render={({ field: { ref: _ref, ...field }, fieldState }) => {
								const { isDirty, invalid } = fieldState;
								const inputProps = {
									id: field.name,
									label: 'Expiration date',
									placeholder: 'dd.mm.yyy',
									type: 'date',
								};
								if (isDirty) {
									inputProps.valid = !invalid;
									inputProps.iconRight = handleResetIcon(field.name);
								}
								const effectiveDate = getValues('effectiveTimestamp');
								if (effectiveDate) {
									inputProps.min = effectiveDate;
									inputProps.max = getLimitDate(effectiveDate);
								} else {
									inputProps.max = getLimitDate();
								}
								return (
									<FormRow>
										<Input {...field} {...inputProps} />
									</FormRow>
								);
							}}
						/>
					)}

					{/* City */}
					<Controller
						name="cityId"
						control={control}
						rules={{ required: true }}
						disabled={loading}
						render={({ field }) => (
							<FormRow>
								<LocationSearch
									onReset={() => resetField(field.name)}
									fieldControl={field}
									setValue={setValue}
								/>
							</FormRow>
						)}
					/>

					{/* Temporary checkbox */}
					<FormRow style={{ textAlign: 'left' }}>
						<Checkbox
							disabled={loading}
							id="set-temporary-location"
							value="temporary"
							label="set as temporary home location"
							checked={isTemporaryHomebase}
							onChange={handleTemporaryCheck}
						/>
					</FormRow>
				</form>
			</PopupSection>

			<PopupSection type="actions">
				<Button onClick={handleCloseAndReset}>Cancel</Button>
				<Button
					appearance="primary"
					onClick={handleConfirm}
					disabled={!Object.keys(dirtyFields).length || !isReadyForSubmission()}
					loading={loading}
				>
					Confirm
				</Button>
			</PopupSection>
		</Popup>
	);
};

export default ModalSetHomeLocation;
